import type { City } from './CityPicker'

/**
 * A request understood from a sentence, then completed a question at a time.
 *
 * Carried between the bubble and the booking screens as router state rather
 * than a query string: it is a whole object, it is not worth a URL anyone
 * would share, and a reload SHOULD clear it -- a prepared booking that
 * survives a refresh starts looking like a booking.
 */
export interface AskDraft {
  kind: 'freight' | 'passenger'
  from?: City
  to?: City
  date?: string
  /** Explicitly answered "any day", which is different from not yet asked. */
  anyDay?: boolean
  items?: { item_type_id: string; code: string; label_key: string; qty: number }[]
  seats?: number
  /** Field names still needed, as the SERVER saw the original sentence. */
  missing: string[]
  unknown: string[]
  /** Real cities named in the sentence that lie outside this client's country. */
  outOfScope?: string[]
  /** The country the vocabulary was limited to, so the card can say so. */
  countryName?: string | null
  understood: string
}

/** The draft this navigation carried, if it carried one. */
export function draftFrom(state: unknown): AskDraft | null {
  const d = (state as { draft?: AskDraft } | null)?.draft
  return d && typeof d === 'object' && 'kind' in d ? d : null
}

/**
 * What still has to be answered, in the order it can be answered.
 *
 * Date first -- it is the one answer that depends on nothing. Then the origin,
 * then the destination (which can only be offered once the origin is known,
 * because the list is "places something really runs to from here"), then what
 * is being sent.
 *
 * Recomputed here rather than read from the server's `missing`: that was a
 * snapshot of the original sentence and goes stale as soon as a chip is
 * tapped.
 */
export function stillMissing(d: AskDraft): string[] {
  const gaps: string[] = []
  if (!d.date && !d.anyDay) gaps.push('date')
  if (!d.from) gaps.push('from')
  if (!d.to) gaps.push('to')
  if (d.kind === 'freight' && !d.items?.length) gaps.push('items')
  return gaps
}
